import { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import {
  AlertCircle, ArrowLeft, Building2, CalendarDays, CheckCircle2, Clock3,
  CreditCard, ExternalLink, FileText, Hash, Link2, Loader2, UserRound,
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const PAYMENT_META = {
  payment_in: {
    title: 'Payment In Details',
    label: 'Payment In',
    partyLabel: 'Customer',
    partyKey: 'customer_name',
    documentLabel: 'Sale Invoice',
    documentPath: '/admin/sale/invoices',
    backPath: '/admin/sale/payment-in',
    amountClass: 'text-green-600',
    amountPrefix: '+',
  },
  payment_out: {
    title: 'Payment Out Details',
    label: 'Payment Out',
    partyLabel: 'Vendor',
    partyKey: 'vendor_name',
    documentLabel: 'Purchase Bill',
    documentPath: '/admin/purchase/bills',
    backPath: '/admin/purchase/payment-out',
    amountClass: 'text-red-600',
    amountPrefix: '-',
  },
};

const STATUS_STYLES = {
  Allocated: 'bg-green-100 text-green-700',
  'Partially Allocated': 'bg-yellow-100 text-yellow-700',
  Unallocated: 'bg-slate-100 text-slate-700',
};

const formatAmount = value => `₹${Number(value || 0).toLocaleString('en-IN', {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
})}`;

const formatDate = value => value
  ? new Date(value).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })
  : '-';

const formatDateTime = value => value
  ? new Date(value).toLocaleString('en-IN', {
      day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit',
    })
  : '-';

const paymentFileUrl = value => {
  if (!value) return '';
  return /^https?:\/\//i.test(value) ? value : `${BACKEND_URL}${value}`;
};

const PaymentDetail = ({ expectedType }) => {
  const { id } = useParams();
  const navigate = useNavigate();
  const meta = PAYMENT_META[expectedType];
  const [payment, setPayment] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    const fetchPayment = async () => {
      try {
        const response = await axios.get(`${BACKEND_URL}/api/transactions/${id}`, {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
        });
        if (response.data.type !== expectedType) {
          setError(`${meta.label} not found`);
          return;
        }
        setPayment(response.data);
      } catch (err) {
        setError(err.response?.data?.detail || `Failed to load ${meta.label.toLowerCase()}`);
      } finally {
        setLoading(false);
      }
    };
    fetchPayment();
  }, [expectedType, id, meta.label]);

  if (loading) {
    return <div className="flex items-center justify-center py-24"><Loader2 className="h-8 w-8 animate-spin text-orange-500" /></div>;
  }

  if (error || !payment) {
    return (
      <div data-testid="payment-detail-error">
        <button onClick={() => navigate(meta.backPath)} className="inline-flex items-center gap-2 text-slate-600 hover:text-slate-900 mb-6">
          <ArrowLeft className="h-4 w-4" /> Back to {meta.label}
        </button>
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-sm flex items-center gap-2">
          <AlertCircle className="h-5 w-5" /> {error || 'Payment not found'}
        </div>
      </div>
    );
  }

  const allocations = payment.allocations || [];
  const debitNoteAllocations = payment.debit_note_allocations || [];
  const activities = [
    {
      key: 'created',
      type: 'created',
      title: `${meta.label} created`,
      description: `${formatAmount(payment.amount)} recorded through ${payment.payment_type || 'the selected payment mode'}.`,
      timestamp: payment.created_at,
    },
    ...allocations.map((allocation, index) => ({
      key: `allocation-${allocation.reference_id}-${index}`,
      type: 'allocation',
      title: `${formatAmount(allocation.amount)} allocated`,
      description: `Allocated to ${meta.documentLabel} ${allocation.reference_label || allocation.reference_id?.slice(0, 8) || '-'}.`,
      timestamp: allocation.allocated_at || payment.updated_at || payment.created_at,
      allocation,
    })),
  ].sort((a, b) => new Date(a.timestamp || 0) - new Date(b.timestamp || 0));

  return (
    <div data-testid={`${expectedType.replace('_', '-')}-detail-page`}>
      <button onClick={() => navigate(meta.backPath)} data-testid="back-btn"
        className="inline-flex items-center gap-2 text-slate-600 hover:text-slate-900 mb-6 transition-colors">
        <ArrowLeft className="h-4 w-4" /> Back to {meta.label}
      </button>

      <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4 mb-6">
        <div>
          <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1">{meta.label}</p>
          <h1 className="text-3xl font-bold text-slate-900 uppercase tracking-tight">{meta.title}</h1>
          <p className="font-mono text-sm text-slate-500 mt-2">{payment.receipt_number || payment.id}</p>
        </div>
        <span className={`inline-flex self-start items-center px-3 py-1.5 rounded-sm text-xs font-bold uppercase ${STATUS_STYLES[payment.allocation_status] || STATUS_STYLES.Unallocated}`}>
          {payment.allocation_status || 'Unallocated'}
        </span>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
        <div className="bg-white border border-slate-200 rounded-sm p-5">
          <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Payment Amount</p>
          <p className={`text-2xl font-bold mt-2 ${meta.amountClass}`}>{meta.amountPrefix} {formatAmount(payment.amount)}</p>
        </div>
        <div className="bg-white border border-slate-200 rounded-sm p-5">
          <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Allocated</p>
          <p className="text-2xl font-bold text-slate-900 mt-2">{formatAmount(payment.allocated_amount)}</p>
        </div>
        <div className="bg-white border border-slate-200 rounded-sm p-5">
          <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Available</p>
          <p className="text-2xl font-bold text-orange-600 mt-2">{formatAmount(payment.unallocated_amount)}</p>
        </div>
      </div>

      {expectedType === 'payment_out' && debitNoteAllocations.length > 0 && (
        <div className="bg-white border border-amber-200 rounded-sm mb-6">
          <div className="px-5 py-4 border-b border-amber-200 bg-amber-50">
            <h2 className="text-sm font-bold text-slate-900 uppercase tracking-wider">Debit Notes Used</h2>
            <p className="text-xs text-slate-500 mt-1">Debit Note balances applied through this Payment Out</p>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead><tr className="border-b border-slate-200 text-xs font-semibold text-slate-500 uppercase"><th className="px-5 py-3 text-left">Debit Note</th><th className="px-5 py-3 text-left">Applied On</th><th className="px-5 py-3 text-right">Amount</th></tr></thead>
              <tbody>{debitNoteAllocations.map((allocation, index) => (
                <tr key={allocation.allocation_id || index} className="border-b border-slate-100 last:border-0">
                  <td className="px-5 py-3"><Link to={`/admin/purchase/return/${allocation.note_id}`} className="inline-flex items-center gap-1 font-mono font-semibold text-orange-600 hover:underline">{allocation.note_number || allocation.note_id}<ExternalLink className="h-3 w-3" /></Link></td>
                  <td className="px-5 py-3 text-slate-600">{formatDateTime(allocation.allocated_at || allocation.note_date)}</td>
                  <td className="px-5 py-3 text-right font-semibold">{formatAmount(allocation.amount)}</td>
                </tr>
              ))}</tbody>
            </table>
          </div>
        </div>
      )}

      <div className="bg-white border border-slate-200 rounded-sm mb-6">
        <div className="px-5 py-4 border-b border-slate-200 bg-slate-50">
          <h2 className="text-sm font-bold text-slate-900 uppercase tracking-wider">Payment Information</h2>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 p-5">
          <DetailField icon={UserRound} label={meta.partyLabel} value={payment[meta.partyKey]} />
          <DetailField icon={Building2} label="Company" value={payment.company_name} />
          <DetailField icon={CreditCard} label="Payment Type" value={payment.payment_type} />
          <DetailField icon={CalendarDays} label="Payment Date" value={formatDate(payment.date)} />
          <DetailField icon={Hash} label="Receipt Number" value={payment.receipt_number} mono />
          <DetailField icon={Hash} label="Reference Number" value={payment.reference_number} mono />
          <DetailField icon={CreditCard} label="Bank Account" value={payment.bank_account_label} />
          <DetailField icon={Clock3} label="Created" value={formatDateTime(payment.created_at)} />
          <DetailField icon={Clock3} label="Last Updated" value={formatDateTime(payment.updated_at)} />
        </div>
        {(payment.description || payment.notes || payment.image_url) && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5 px-5 pb-5 border-t border-slate-100 pt-5">
            {payment.description && <DetailField icon={FileText} label="Description" value={payment.description} />}
            {payment.notes && <DetailField icon={FileText} label="Notes" value={payment.notes} />}
            {payment.image_url && (
              <div>
                <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1">Attachment</p>
                <a href={paymentFileUrl(payment.image_url)} target="_blank" rel="noreferrer"
                  className="inline-flex items-center gap-1 text-sm font-semibold text-orange-600 hover:text-orange-700">
                  View attachment <ExternalLink className="h-3.5 w-3.5" />
                </a>
              </div>
            )}
          </div>
        )}
      </div>

      <div className="bg-white border border-slate-200 rounded-sm mb-6">
        <div className="px-5 py-4 border-b border-slate-200 bg-slate-50">
          <h2 className="text-sm font-bold text-slate-900 uppercase tracking-wider">Allocations</h2>
          <p className="text-xs text-slate-500 mt-1">Every {meta.documentLabel.toLowerCase()} linked to this payment</p>
        </div>
        {allocations.length === 0 ? (
          <div className="px-5 py-10 text-center text-sm text-slate-400">No allocations have been made yet.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-slate-200 text-xs font-semibold text-slate-500 uppercase">
                  <th className="px-5 py-3 text-left">{meta.documentLabel}</th>
                  <th className="px-5 py-3 text-right">Allocated Amount</th>
                  <th className="px-5 py-3 text-left">Allocated On</th>
                </tr>
              </thead>
              <tbody>
                {allocations.map((allocation, index) => (
                  <tr key={`${allocation.reference_id}-${index}`} className="border-b border-slate-100 last:border-0">
                    <td className="px-5 py-3">
                      <Link to={`${meta.documentPath}/${allocation.reference_id}`}
                        className="inline-flex items-center gap-1 font-mono font-semibold text-orange-600 hover:text-orange-700 hover:underline">
                        {allocation.reference_label || allocation.reference_id?.slice(0, 8) || '-'}
                        <ExternalLink className="h-3 w-3" />
                      </Link>
                    </td>
                    <td className="px-5 py-3 text-right font-semibold text-slate-900">{formatAmount(allocation.amount)}</td>
                    <td className="px-5 py-3 text-slate-600">{formatDateTime(allocation.allocated_at || payment.updated_at)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <div className="bg-white border border-slate-200 rounded-sm">
        <div className="px-5 py-4 border-b border-slate-200 bg-slate-50">
          <h2 className="text-sm font-bold text-slate-900 uppercase tracking-wider">Activity Timeline</h2>
          <p className="text-xs text-slate-500 mt-1">Creation and allocation history for this payment</p>
        </div>
        <ol className="relative p-6">
          <div className="absolute left-9 top-6 bottom-6 w-px bg-slate-200" aria-hidden="true" />
          {activities.map(activity => {
            const isAllocation = activity.type === 'allocation';
            const Icon = isAllocation ? Link2 : CheckCircle2;
            return (
              <li key={activity.key} className="relative pl-10 pb-7 last:pb-0">
                <span className={`absolute left-1.5 top-0 w-7 h-7 rounded-full bg-white ring-4 flex items-center justify-center ${isAllocation ? 'ring-orange-200' : 'ring-green-200'}`}>
                  <Icon className={`h-3.5 w-3.5 ${isAllocation ? 'text-orange-600' : 'text-green-600'}`} />
                </span>
                <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-1 sm:gap-4">
                  <div>
                    <h3 className="text-sm font-bold text-slate-900">{activity.title}</h3>
                    <p className="text-sm text-slate-600 mt-0.5">{activity.description}</p>
                    {activity.allocation && (
                      <Link to={`${meta.documentPath}/${activity.allocation.reference_id}`}
                        className="inline-flex items-center gap-1 text-xs font-semibold font-mono text-orange-600 hover:text-orange-700 mt-2">
                        {activity.allocation.reference_label || activity.allocation.reference_id?.slice(0, 8)}
                        <ExternalLink className="h-3 w-3" />
                      </Link>
                    )}
                  </div>
                  <time className="text-xs text-slate-400 whitespace-nowrap">{formatDateTime(activity.timestamp)}</time>
                </div>
              </li>
            );
          })}
        </ol>
      </div>
    </div>
  );
};

const DetailField = ({ icon: Icon, label, value, mono = false }) => (
  <div>
    <p className="flex items-center gap-1.5 text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1">
      <Icon className="h-3.5 w-3.5" /> {label}
    </p>
    <p className={`text-sm font-medium text-slate-900 break-words ${mono ? 'font-mono' : ''}`}>{value || '-'}</p>
  </div>
);

export const PaymentInDetail = () => <PaymentDetail expectedType="payment_in" />;
export const PaymentOutDetail = () => <PaymentDetail expectedType="payment_out" />;
